import { useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUpRight,
  BadgeCheck,
  Clock,
  FileText,
  Gavel,
  Home,
  Loader2,
  MapPin,
  Satellite,
  Scale,
  ShieldCheck,
  Users,
} from "lucide-react";
import { NetworkBackground } from "@/components/landing/NetworkBackground";
import { MaskedReveal, FadeUp } from "@/components/landing/MaskedReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATS = [
  { v: "10+", l: "Years serving NY" },
  { v: "100%", l: "GPS-tracked serves" },
  { v: "24/7", l: "Rush availability" },
  { v: "All 5", l: "Boroughs covered" },
];

const SERVICES = [
  {
    icon: FileText,
    title: "Summons & Complaints",
    desc: "Timely, affidavit-ready service for civil actions across all NY courts.",
    span: "lg:col-span-5",
    tall: true,
  },
  {
    icon: Gavel,
    title: "Subpoenas",
    desc: "Witness, deposition and records subpoenas handled with discretion and speed.",
    span: "lg:col-span-4",
  },
  {
    icon: Home,
    title: "Landlord / Tenant",
    desc: "3-day, 10-day, 30-day and 90-day notices. Evictions and holdover petitions.",
    span: "lg:col-span-3",
  },
  {
    icon: Scale,
    title: "Divorce & Family",
    desc: "Sensitive, confidential personal service of divorce and family-court papers.",
    span: "lg:col-span-4",
  },
  {
    icon: Users,
    title: "Small Claims & Civil",
    desc: "Service for small-claims petitions, orders to show cause and motions.",
    span: "lg:col-span-4",
  },
  {
    icon: Clock,
    title: "Rush & Same-Day",
    desc: "Priority dispatch when the clock is already running. Documented every step.",
    span: "lg:col-span-4",
  },
];

const COVERAGE = [
  "Manhattan",
  "Brooklyn",
  "Queens",
  "The Bronx",
  "Staten Island",
  "Nassau County",
  "Suffolk County",
  "Westchester",
  "Rockland",
  "Orange",
  "Putnam",
  "Dutchess",
];

const Card = ({ s, i }) => {
  const Icon = s.icon;
  return (
    <FadeUp
      delay={i * 0.05}
      testId={`ps-service-${i}`}
      className={`${s.span} group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0A0A0C] p-8 md:p-10 transition-all duration-500 hover:-translate-y-2 hover:border-[#00E5FF]/40 ${
        s.tall ? "lg:row-span-2 flex flex-col justify-between min-h-[360px]" : "min-h-[220px]"
      }`}
    >
      <div className="relative z-10 flex items-start justify-between">
        <div className="h-12 w-12 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center text-[#00E5FF]">
          <Icon size={22} />
        </div>
        <ArrowUpRight
          size={20}
          className="text-white/20 transition-all duration-300 group-hover:text-[#00E5FF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        />
      </div>
      <div className="relative z-10 mt-8">
        <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-white">
          {s.title}
        </h3>
        <p className="mt-3 text-[#A0AEC0] leading-relaxed max-w-md">{s.desc}</p>
      </div>
    </FadeUp>
  );
};

const inputClass =
  "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-[#5A6472] focus:border-[#00E5FF] focus:outline-none transition-colors duration-300";

const MiniContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    caseNumber: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and service details.");
      return;
    }
    setLoading(true);
    try {
      const composed = form.caseNumber
        ? `Case / Index #: ${form.caseNumber}\n\n${form.message}`
        : form.message;
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company,
        service: "NY Process Serving",
        message: composed,
      });
      toast.success(data.message || "Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", caseNumber: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="ps-contact-form"
      className="rounded-2xl border border-white/10 bg-[#050505] p-8 md:p-10"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <input
          data-testid="ps-contact-name"
          className={inputClass}
          placeholder="Your name *"
          value={form.name}
          onChange={update("name")}
        />
        <input
          data-testid="ps-contact-email"
          type="email"
          className={inputClass}
          placeholder="Email *"
          value={form.email}
          onChange={update("email")}
        />
        <input
          data-testid="ps-contact-company"
          className={inputClass}
          placeholder="Firm / Agency"
          value={form.company}
          onChange={update("company")}
        />
        <input
          data-testid="ps-contact-case"
          className={inputClass}
          placeholder="Case / Index number (optional)"
          value={form.caseNumber}
          onChange={update("caseNumber")}
        />
        <textarea
          data-testid="ps-contact-message"
          rows={3}
          className={`${inputClass} resize-none md:col-span-2`}
          placeholder="Service details, deadline, preferred location *"
          value={form.message}
          onChange={update("message")}
        />
      </div>
      <button
        type="submit"
        data-testid="ps-contact-submit"
        disabled={loading}
        className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#00E5FF] text-[#050505] px-8 py-4 font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-60"
      >
        {loading ? (
          <>
            <Loader2 size={18} className="animate-spin" /> Sending…
          </>
        ) : (
          <>
            Request a serve
            <ArrowUpRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </>
        )}
      </button>
    </form>
  );
};

export const ProcessServing = ({ onNavigate }) => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="process-serving-page">
      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="ps-hero"
        className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]"
      >
        <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
          <NetworkBackground />
        </motion.div>
        <div
          className="absolute inset-0 z-[1] pointer-events-none"
          style={{
            background:
              "radial-gradient(120% 90% at 50% 10%, rgba(5,5,5,0) 40%, rgba(5,5,5,0.85) 100%)",
          }}
        />

        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 min-h-[100svh] flex flex-col justify-center pt-24 pb-16"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-xs uppercase tracking-[0.28em] text-[#00E5FF] font-bold mb-7 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-[#00E5FF]/60" />
            Licensed · GPS-tracked · Statewide
          </motion.p>

          <h1 className="font-black tracking-tighter text-white text-5xl md:text-7xl lg:text-[6rem] leading-[0.92] max-w-[15ch]" data-testid="ps-hero-heading">
            {["NY Process", "Serving."].map((t, i) => (
              <span key={i} className="block overflow-hidden" style={{ paddingBottom: "0.08em" }}>
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.5 + i * 0.14 }}
                >
                  {i === 1 ? (
                    <span>
                      <span className="text-[#00E5FF] glow-cyan">Serving.</span>
                    </span>
                  ) : (
                    t
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-8 max-w-xl text-[#A0AEC0] text-base md:text-lg leading-relaxed"
          >
            Ten-plus years serving law firms, agencies and businesses across New
            York State. Every serve is tracked with GPS and documented through
            industry-standard platforms — so your affidavit stands up the first
            time it's questioned.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="ps-hero-cta"
              onClick={() => onNavigate?.("#ps-contact")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#00E5FF] text-[#050505] px-7 py-4 font-semibold hover:bg-white transition-colors duration-300"
            >
              Request a serve
              <ArrowUpRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </button>
            <button
              data-testid="ps-hero-services"
              onClick={() => onNavigate?.("#ps-services")}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white px-7 py-4 font-medium hover:border-white/40 hover:bg-white/5 transition-all duration-300"
            >
              See what we serve
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ opacity: heroOpacity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#5A6472]"
        >
          <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          >
            <ArrowDown size={16} />
          </motion.div>
        </motion.div>
      </section>

      {/* STATS */}
      <section
        data-testid="ps-stats"
        className="relative bg-[#0A0A0C] border-y border-white/10 py-16 md:py-20"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-8">
          {STATS.map((s, i) => (
            <FadeUp key={i} delay={i * 0.05} className="flex flex-col">
              <div className="text-4xl md:text-6xl font-black tracking-tighter text-white">
                {s.v}
              </div>
              <div className="mt-3 h-px w-12 bg-[#00E5FF]/50" />
              <p className="mt-4 text-sm md:text-base text-[#A0AEC0] uppercase tracking-wide">
                {s.l}
              </p>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section
        id="ps-services"
        data-testid="ps-services-section"
        className="relative bg-[#050505] py-24 md:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-[#00E5FF] font-bold mb-6">
                What we serve
              </p>
              <MaskedReveal
                as="h2"
                testId="ps-services-heading"
                lines={["Every paper that", "needs a signature."]}
                className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[0.95]"
              />
            </div>
            <p className="max-w-sm text-[#A0AEC0] leading-relaxed">
              From civil summons to sensitive family matters, our licensed
              servers deliver with care, speed and a paper trail that holds up
              in court.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 auto-rows-auto">
            {SERVICES.map((s, i) => (
              <Card key={i} s={s} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE / TECH */}
      <section
        id="ps-compliance"
        data-testid="ps-compliance"
        className="relative bg-[#0A0A0C] border-y border-white/10 py-24 md:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.28em] text-[#00E5FF] font-bold mb-6">
              Compliance & technology
            </p>
            <MaskedReveal
              as="h2"
              testId="ps-compliance-heading"
              lines={["Documented at", "every step."]}
              className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[0.95]"
            />
            <p className="mt-8 text-[#A0AEC0] leading-relaxed max-w-md">
              New York requires GPS-tracked service for a reason — because the
              defense will look. We go beyond the minimum, pairing a
              DCWP-compliant GPS vendor with industry-standard case software so
              every serve is time-stamped, geo-verified, and auditable in
              seconds.
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
            <FadeUp
              testId="ps-tech-tis"
              className="rounded-2xl border border-white/10 bg-[#050505] p-8 flex flex-col hover:border-[#00E5FF]/30 transition-colors duration-500"
            >
              <div className="h-12 w-12 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center text-[#00E5FF] mb-6">
                <Satellite size={22} />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#00E5FF] font-bold mb-2">
                GPS Vendor
              </p>
              <h3 className="text-2xl font-bold tracking-tight text-white">
                The Independent Server
              </h3>
              <p className="mt-3 text-[#A0AEC0] leading-relaxed">
                Independent, third-party GPS verification on every attempt —
                location, timestamp and movement history recorded for the full
                life of the serve.
              </p>
            </FadeUp>

            <FadeUp
              delay={0.08}
              testId="ps-tech-sm"
              className="rounded-2xl border border-white/10 bg-[#050505] p-8 flex flex-col hover:border-[#00E5FF]/30 transition-colors duration-500"
            >
              <div className="h-12 w-12 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center text-[#00E5FF] mb-6">
                <FileText size={22} />
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#00E5FF] font-bold mb-2">
                Case Management
              </p>
              <h3 className="text-2xl font-bold tracking-tight text-white">
                ServeManager
              </h3>
              <p className="mt-3 text-[#A0AEC0] leading-relaxed">
                Real-time job status, photo-documented attempts, instant
                affidavits and secure document delivery — the industry standard
                for law-firm visibility.
              </p>
            </FadeUp>

            <FadeUp
              delay={0.14}
              testId="ps-tech-licensed"
              className="rounded-2xl border border-white/10 bg-[#050505] p-8 flex flex-col hover:border-[#00E5FF]/30 transition-colors duration-500 md:col-span-2"
            >
              <div className="flex items-start gap-6">
                <div className="h-12 w-12 shrink-0 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center text-[#00E5FF]">
                  <BadgeCheck size={22} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#00E5FF] font-bold mb-2">
                    Credentials
                  </p>
                  <h3 className="text-2xl font-bold tracking-tight text-white">
                    Licensed, bonded & fully insured
                  </h3>
                  <p className="mt-3 text-[#A0AEC0] leading-relaxed max-w-2xl">
                    Compliant with NYC DCWP process-server licensing, NY General
                    Business Law and CPLR service rules. Over a decade of
                    uncontested affidavits across civil, family, housing and
                    commercial matters.
                  </p>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section
        id="ps-coverage"
        data-testid="ps-coverage"
        className="relative bg-[#050505] py-24 md:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="mb-14 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.28em] text-[#00E5FF] font-bold mb-6">
              Where we serve
            </p>
            <MaskedReveal
              as="h2"
              testId="ps-coverage-heading"
              lines={["Statewide reach,", "local knowledge."]}
              className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[0.95]"
            />
            <p className="mt-6 text-[#A0AEC0] leading-relaxed">
              Headquartered in New York with on-the-ground servers in every
              borough and the surrounding counties. Need something outside the
              region? We coordinate nationwide through a vetted partner network.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {COVERAGE.map((c, i) => (
              <FadeUp key={c} delay={i * 0.03}>
                <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[#0A0A0C] px-5 py-2.5 text-sm text-white hover:border-[#00E5FF]/40 hover:text-[#00E5FF] transition-colors duration-300">
                  <MapPin size={14} className="text-[#00E5FF]" />
                  {c}
                </span>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="ps-contact"
        data-testid="ps-contact-section"
        className="relative bg-[#0A0A0C] border-t border-white/10 py-24 md:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.28em] text-[#00E5FF] font-bold mb-6">
              Request a serve
            </p>
            <MaskedReveal
              as="h2"
              testId="ps-contact-heading"
              lines={["Send us the papers.", "We'll handle the rest."]}
              className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter text-white leading-[0.95]"
            />
            <p className="mt-8 text-[#A0AEC0] leading-relaxed max-w-md">
              Tell us the case, the deadline, and where service needs to happen.
              A real person responds within one business day — same-day for
              rush requests.
            </p>

            <div className="mt-10 space-y-4 text-[#A0AEC0]">
              <div className="flex items-center gap-3">
                <ShieldCheck size={18} className="text-[#00E5FF]" />
                Licensed · Bonded · Insured
              </div>
              <div className="flex items-center gap-3">
                <Satellite size={18} className="text-[#00E5FF]" />
                GPS verified via The Independent Server
              </div>
              <div className="flex items-center gap-3">
                <FileText size={18} className="text-[#00E5FF]" />
                Live tracking via ServeManager
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <MiniContactForm />
          </div>
        </div>
      </section>
    </div>
  );
};
